// Ported verbatim from mobile src/utils/brainerCoreTransform.ts (import paths only).
import type { BrainerCoreInner, BrainerCoreSignal } from 'types/brainerCore';
import type {
  TFCardData,
  MetricItem,
  SignalItem,
  SignalGroups,
  VWAPItem,
  LevelItem,
} from 'data/analysisDisplay';

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatPrice(value: number): string {
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function pricePct(price: number, ref: number): string {
  const pct = ((price - ref) / ref) * 100;
  return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
}

function dirArrow(dir: string): string {
  const up = dir?.toUpperCase();
  if (up === 'RISING') return '↑';
  if (up === 'FALLING') return '↓';
  return '→';
}

// ─── TF Cards ────────────────────────────────────────────────────────────────

function signalsToSignalItems(signals: BrainerCoreSignal[]): SignalItem[] {
  return signals.map((s) => ({
    label: s.name,
    age: s.bars_ago === 0 ? 'Now' : s.age_text,
    color: s.direction === 'bullish' ? '#00E87A' : '#FF5A5A',
    isNow: s.bars_ago === 0,
  }));
}

function buildCtfCard(d: BrainerCoreInner): TFCardData {
  const metrics: MetricItem[] = [
    {
      label: 'BrainWaves',
      value: `${d.bw_current} ${dirArrow(d.bw_current_dir)}`,
      sub: d.bw_current_ribbon.charAt(0) + d.bw_current_ribbon.slice(1).toLowerCase(),
      color: '#00C2D4',
    },
    {
      label: 'RSI',
      value: `${d.rsi} ${dirArrow(d.rsi_dir)}`,
      sub: d.rsi_dir.charAt(0) + d.rsi_dir.slice(1).toLowerCase(),
    },
    {
      label: 'Delta',
      value: String(d.delta),
      sub: `${d.delta_status} · ${d.delta_trend}`,
      color: d.delta < 0 ? '#FF5A5A' : '#00E87A',
      barPercent: Math.min(Math.abs(d.delta) / 10, 100),
      barColor: d.delta < 0 ? '#FF5A5A' : '#00E87A',
    },
    {
      label: 'Exhaustion',
      value: `${d.exhaustion}%`,
      sub: d.exhaustion >= 70 ? 'High · Caution' : d.exhaustion >= 50 ? 'Moderate' : 'Low',
      color: '#A855F7',
      barPercent: d.exhaustion,
      barColor: '#A855F7',
    },
    {
      label: 'Efficiency',
      value: `${d.efficiency}%`,
      sub: d.efficiency >= 200 ? 'Explosive' : d.efficiency >= 100 ? 'Strong' : 'Weak',
    },
    {
      label: 'Conviction',
      value: `${d.conviction_pct}% ${d.conviction_dir.charAt(0) + d.conviction_dir.slice(1).toLowerCase()}`,
      sub: d.conviction_level.charAt(0) + d.conviction_level.slice(1).toLowerCase(),
      color: '#00C2D4',
      barPercent: d.conviction_pct,
      barColor: '#00C2D4',
    },
    {
      label: 'Momentum',
      value: `${d.momentum > 0 ? '+' : ''}${d.momentum}`,
      sub: d.momentum_accel,
      color: d.momentum < 0 ? '#FF5A5A' : '#00E87A',
    },
    {
      label: 'Sigma',
      value: `${d.sigma > 0 ? '+' : ''}${d.sigma}σ`,
      sub: Math.abs(d.sigma) < 1 ? 'Normal' : Math.abs(d.sigma) < 2 ? 'Elevated' : 'Extreme',
    },
  ];

  const signals: SignalGroups = {
    bullish: signalsToSignalItems([...d.bullish_signals].sort((a, b) => a.bars_ago - b.bars_ago)),
    bearish: signalsToSignalItems([...d.bearish_signals].sort((a, b) => a.bars_ago - b.bars_ago)),
  };

  return {
    role: 'Current timeframe',
    name: d.ctf_tf,
    isBullish: d.ctf_trend === 'BULLISH',
    gradientColors:
      d.ctf_trend === 'BULLISH'
        ? ['#00C2D4', '#00E87A', '#00C2D4']
        : ['#FF5A5A', '#FF3D3D', '#FF5A5A'],
    metrics,
    signals,
  };
}

function buildLtfCard(d: BrainerCoreInner): TFCardData {
  const metrics: MetricItem[] = [
    {
      label: 'BrainWaves',
      value: `${d.bw_lower} ${dirArrow(d.bw_lower_dir)}`,
      sub: d.bw_lower_ribbon.charAt(0) + d.bw_lower_ribbon.slice(1).toLowerCase(),
      color: '#00C2D4',
    },
    {
      label: 'RSI',
      value: `${d.rsi_ltf} ${dirArrow(d.rsi_ltf_dir)}`,
      sub: d.rsi_ltf_dir.charAt(0) + d.rsi_ltf_dir.slice(1).toLowerCase(),
    },
    {
      label: 'Delta',
      value: String(d.ltf_delta),
      sub: `${d.ltf_delta_status} · ${d.ltf_delta_trend}`,
      color: d.ltf_delta < 0 ? '#FF5A5A' : '#00E87A',
      barPercent: Math.min(Math.abs(d.ltf_delta) / 10, 100),
      barColor: d.ltf_delta < 0 ? '#FF5A5A' : '#00E87A',
    },
  ];

  return {
    role: 'Lower timeframe',
    name: d.ltf_tf,
    isBullish: d.ltf_trend === 'BULLISH',
    gradientColors:
      d.ltf_trend === 'BULLISH'
        ? ['#00BFFF', '#00E87A']
        : ['#FF5A5A', '#FF3D3D', '#FF5A5A'],
    metrics,
    signals: { bullish: [], bearish: [] },
  };
}

function buildHtfCard(d: BrainerCoreInner): TFCardData {
  const metrics: MetricItem[] = [
    {
      label: 'BrainWaves',
      value: `${d.bw_higher} ${dirArrow(d.bw_higher_dir)}`,
      sub: d.bw_higher_ribbon.charAt(0) + d.bw_higher_ribbon.slice(1).toLowerCase(),
      color: '#00C2D4',
    },
    {
      label: 'RSI',
      value: `${d.rsi_htf} ${dirArrow(d.rsi_htf_dir)}`,
      sub: d.rsi_htf_dir.charAt(0) + d.rsi_htf_dir.slice(1).toLowerCase(),
    },
    {
      label: 'Delta',
      value: String(d.htf_delta),
      sub: `${d.htf_delta_status} · ${d.htf_delta_trend}`,
      color: d.htf_delta < 0 ? '#FF5A5A' : '#00E87A',
      barPercent: Math.min(Math.abs(d.htf_delta) / 10, 100),
      barColor: d.htf_delta < 0 ? '#FF5A5A' : '#00E87A',
    },
  ];

  return {
    role: 'Higher timeframe',
    name: d.htf_tf,
    isBullish: d.htf_trend === 'BULLISH',
    gradientColors:
      d.htf_trend === 'BULLISH'
        ? ['#00BFFF', '#00E87A']
        : ['#FF5A5A', '#FF3D3D', '#FF5A5A'],
    metrics,
    signals: { bullish: [], bearish: [] },
  };
}

export function transformTFCards(d: BrainerCoreInner): TFCardData[] {
  return [buildCtfCard(d), buildLtfCard(d), buildHtfCard(d)];
}

// ─── VWAP ─────────────────────────────────────────────────────────────────────

export function transformVWAP(d: BrainerCoreInner): VWAPItem[] {
  function vwapItem(label: string, vwap: number, sd: number, isFullWidth?: boolean): VWAPItem {
    const isAbove = d.price >= vwap;
    const pct = pricePct(d.price, vwap);
    const sdLabel = `${sd > 0 ? '+' : ''}${sd} SD`;
    return {
      label,
      value: isAbove ? 'Above' : 'Below',
      sub: `${pct} · ${sdLabel}`,
      isAbove,
      isFullWidth,
    };
  }

  return [
    vwapItem('Daily', d.d_vwap, d.d_vwap_sd),
    vwapItem('Weekly', d.w_vwap, d.w_vwap_sd),
    vwapItem('Monthly', d.m_vwap, d.m_vwap_sd, true),
  ];
}

// ─── Key Levels ───────────────────────────────────────────────────────────────

export function transformKeyLevels(d: BrainerCoreInner): {
  resistance: LevelItem[];
  support: LevelItem[];
} {
  function toLevel(
    l: BrainerCoreInner['resistance_levels'][number],
    idx: number,
    prefix: string,
  ): LevelItem {
    const label = l?.components?.length > 0 ? l.components?.join(' + ') : `${prefix}${idx + 1}`;
    return {
      label,
      value: formatPrice(l.low),
      rangeTo: l.high !== l.low ? `→ ${formatPrice(l.high)}` : undefined,
    };
  }

  return {
    resistance: d.resistance_levels.map((l, i) => toLevel(l, i, 'R')),
    support: d.support_levels.map((l, i) => toLevel(l, i, 'S')),
  };
}

export function pickNearestLevels(d: BrainerCoreInner): {
  resistance?: string;
  support?: string;
} {
  const price = d.price;
  const resLevels = d.resistance_levels ?? [];
  const supLevels = d.support_levels ?? [];

  let resistance: string | undefined;
  if (resLevels.length > 0) {
    const above = resLevels.filter((l) => l.low >= price);
    const pick =
      above.length > 0
        ? above.reduce((a, b) => (a.low < b.low ? a : b))
        : resLevels.reduce((a, b) => (a.low > b.low ? a : b));
    resistance = `R ${formatPrice(pick.low)}`;
  }

  let support: string | undefined;
  if (supLevels.length > 0) {
    const below = supLevels.filter((l) => l.high <= price);
    const pick =
      below.length > 0
        ? below.reduce((a, b) => (a.high > b.high ? a : b))
        : supLevels.reduce((a, b) => (a.high < b.high ? a : b));
    support = `S ${formatPrice(pick.high)}`;
  }

  return { resistance, support };
}

// ─── CME Gap ──────────────────────────────────────────────────────────────────

export interface CmeGapDisplay {
  status: string;
  range: string;
  note: string;
}

export function transformCmeGap(d: BrainerCoreInner): CmeGapDisplay | null {
  const gap = d.cme_gap_above ?? d.cme_gap_below;
  if (!gap) return null;

  const [low, high, days] = gap;
  const direction = d.cme_gap_above ? 'Above' : 'Below';
  const note = d.cme_gap_above ? 'Potential magnet upside' : 'Potential magnet downside';

  return {
    status: `${direction} · ${days} days`,
    range: `${formatPrice(low)} → ${formatPrice(high)}`,
    note,
  };
}

// ─── Price display ────────────────────────────────────────────────────────────

export function splitPrice(price: number): { major: string; cents: string } {
  const [wholePart, decimalPart = '00'] = price.toFixed(2).split('.');
  const major = '$' + Number(wholePart).toLocaleString('en-US');
  return { major, cents: '.' + decimalPart };
}
